import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dealService } from '../services';
import { DealDTO, DealStatus } from '../types';
import TermsAndConditions from '../components/TermsAndConditions';
import { useMediaQuery } from '../hooks/useMediaQuery';

const PaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [deal, setDeal] = useState<DealDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [terms, setTerms] = useState({
    acceptTerms: false,
    acceptPrivacy: false,
    acceptPersonalData: false,
    acceptPlatformTerms: false
  });

  useEffect(() => {
    if (!id) {
      setError('ID сделки не указан');
      setLoading(false);
      return;
    }

    const loadDeal = async () => {
      try {
        setLoading(true);
        const dealData = await dealService.getDealById(id);
        
        if (!dealData) {
          setError('Сделка не найдена');
          return;
        }

        setDeal(dealData);
        
        // Если сделка уже зарегистрирована, устанавливаем email
        if (dealData.receiptEmail) {
          setReceiptEmail(dealData.receiptEmail);
        }
      } catch (err) {
        setError('Ошибка при загрузке сделки');
        console.error('Failed to load deal:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDeal();
  }, [id]);

  const handleTermsChange = (field: keyof typeof terms, value: boolean) => {
    setTerms(prev => ({ ...prev, [field]: value }));
  };

  const handlePay = async () => {
    if (!deal || !receiptEmail.trim()) {
      return;
    }

    if (!terms.acceptTerms) {
      return;
    }

    try {
      setPaying(true);
      setPaymentError(null);
      const result = await dealService.payDeal(deal.id, receiptEmail.trim());
      
      if (result?.url) {
        // Если получен URL, переходим на него
        window.location.href = result.url;
      } else {
        setPaymentError(result?.message || 'Оплата временно недоступна: сервис прекратил приём платежей.');
      }
    } catch (err) {
      console.error('Payment failed:', err);
      setPaymentError('Оплата временно недоступна: сервис прекратил приём платежей.');
    } finally {
      setPaying(false);
    }
  };

  const formatAmount = (amount: number): string => {
    const amountInRubles = amount / 100;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2
    }).format(amountInRubles);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141826] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white border-l-2 border-white/20"></div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-[#141826] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-white">Ошибка</h3>
            <p className="mt-1 text-sm text-white/70">{error || 'Сделка не найдена'}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-full text-gray-800 bg-white hover:bg-white/90 transition-colors"
              >
                Вернуться на главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Проверяем, можно ли оплачивать сделку
  if (deal.status !== DealStatus.New && deal.status !== DealStatus.Registered) {
    const getStatusText = (status: DealStatus): string => {
      switch (status) {
        case DealStatus.New: return 'Новая';
        case DealStatus.Registered: return 'Зарегистрирована';
        case DealStatus.Paid: return 'Оплачена';
        case DealStatus.Finished: return 'Завершена';
        case DealStatus.Rejected: return 'Отклонена';
        case DealStatus.Refunded: return 'Возвращена';
        case DealStatus.Deleted: return 'Удалена';
        case DealStatus.Wait: return 'В процессе';
        default: return status;
      }
    };

    return (
      <div className="min-h-screen bg-[#141826] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-white">Сделка недоступна для оплаты</h3>
            <p className="mt-1 text-sm text-white/70">
              Статус сделки: {getStatusText(deal.status)}
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-full text-gray-800 bg-white hover:bg-white/90 transition-colors"
              >
                Вернуться на главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Мобильная версия
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#141826] relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute w-full h-full left-0 top-0 pointer-events-none">
          {/* Ellipse 1 - Top Left */}
          <div 
            className="absolute w-[180px] h-[100px] left-[-80px] top-[80px] bg-[rgba(255,255,255,0.1)] opacity-60 blur-[35px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%, transparent 100%)',
              filter: 'blur(35px)'
            }}
          />
          
          {/* Ellipse 2 - Top Right */}
          <div 
            className="absolute w-[160px] h-[120px] right-[-60px] top-[120px] bg-[rgba(0,0,0,0.2)] opacity-50 blur-[30px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
              filter: 'blur(30px)'
            }}
          />
          
          {/* Ellipse 3 - Bottom Left */}
          <div 
            className="absolute w-[140px] h-[180px] left-[-50px] bottom-[120px] bg-[rgba(255,255,255,0.08)] opacity-40 blur-[25px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 50%, transparent 100%)',
              filter: 'blur(25px)'
            }}
          />
          
          {/* Ellipse 4 - Bottom Right */}
          <div 
            className="absolute w-[120px] h-[100px] right-[-30px] bottom-[150px] bg-[rgba(0,0,0,0.15)] opacity-30 blur-[20px] rounded-full"
            style={{
              background: 'radial-gradient(ellipse, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.08) 65%, transparent 100%)',
              filter: 'blur(20px)'
            }}
          />
        </div>

        {/* Header */}
        <div className="px-4 py-6 flex items-center justify-between relative z-10">
        <div className="w-12 h-12 bg-[#7661d7] rounded-full flex items-center justify-center">
          <span className="text-white text-xl">A</span>
        </div>
          <div className="text-white text-2xl">Сделка и Точка</div>
        </div>

        {/* Main Content */}
        <div className="flex-1 px-4 pb-6 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">{deal.purpose}</h2>
            <p className="text-slate-400 text-sm">Оплата сделки</p>
          </div>

          {/* Deal Info */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/10">
            <h3 className="text-white text-xl mb-4">Детали сделки</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-sm">Сумма:</span>
                <span className="text-white text-2xl">{formatAmount(deal.amount)}</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/10">
            <div className="space-y-6">
              <div>
                <label htmlFor="receiptEmail" className="block text-white text-sm mb-2">
                  Email для чека {deal.status === DealStatus.New ? '*' : ''}
                </label>
                <input
                  type="email"
                  id="receiptEmail"
                  value={receiptEmail}
                  onChange={(e) => setReceiptEmail(e.target.value)}
                  disabled={deal.status === DealStatus.Registered}
                  className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20 transition-colors ${
                    deal.status === DealStatus.Registered ? 'bg-white/10 cursor-not-allowed' : ''
                  }`}
                  placeholder="example@email.com"
                  required={deal.status === DealStatus.New}
                />
                {deal.status === DealStatus.Registered && (
                  <p className="text-xs text-slate-400 mt-2">
                    Email уже установлен и не может быть изменен
                  </p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="border-t border-white/10 pt-4">
                <TermsAndConditions
                  terms={terms}
                  onTermsChange={handleTermsChange}
                  disabled={paying}
                />
              </div>

              {paymentError && (
                <p className="mt-4 text-sm text-red-300">{paymentError}</p>
              )}
              <p className="mt-4 text-sm text-amber-200/90">
                Оплата временно недоступна: сервис прекратил приём платежей.
              </p>

              {/* Payment Button */}
              <button
                onClick={handlePay}
                disabled
                className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white/10 text-white/50 cursor-not-allowed"
              >
                <span>Оплата недоступна</span>
              </button>
            </div>
          </div>

          {/* Security Info */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 border border-white/10">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 text-white text-sm leading-tight">
                Оплата производится через защищенное соединение. Ваши данные защищены.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Десктопная версия (в стиле DesktopLogin)
  return (
    <div className="min-h-screen bg-[#141826] flex">
      {/* Left Panel - Logo and Deal Info */}
      <div className="w-1/2 bg-gradient-to-br from-[#1a1f2e] to-[#141826] flex flex-col justify-center items-center p-12">
        <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-[#7661d7] rounded-full flex items-center justify-center mb-8">
            <span className="text-white text-4xl">A</span>
          </div>
          <h1 className="text-white text-5xl mb-4">Сделка и Точка</h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">
            Безопасная оплата сделок через защищенное соединение
          </p>
          
          {/* Deal Summary */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
            <h3 className="text-white text-2xl mb-4">Детали сделки</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-base">Сумма:</span>
                <span className="text-white text-3xl">{formatAmount(deal.amount)}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-white/70 text-base">Назначение:</span>
                <span className="text-white text-base text-right flex-1 ml-4">{deal.purpose}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Payment Form */}
      <div className="w-1/2 bg-[#141826] flex items-center justify-center p-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">Оплата сделки</h2>
            <p className="text-slate-400 text-base">
              Заполните форму для завершения оплаты
            </p>
          </div>

          {/* Payment Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 mb-6">
            {/* Email Input */}
            <div className="mb-6">
              <label htmlFor="receiptEmail" className="block text-white text-sm mb-2">
                Email для чека {deal.status === DealStatus.New ? '*' : ''}
              </label>
              <input
                type="email"
                id="receiptEmail"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                disabled={deal.status === DealStatus.Registered}
                className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-white/20 transition-colors ${
                  deal.status === DealStatus.Registered ? 'bg-white/10 cursor-not-allowed' : ''
                }`}
                placeholder="example@email.com"
                required={deal.status === DealStatus.New}
              />
              {deal.status === DealStatus.Registered && (
                <p className="text-xs text-slate-400 mt-2">
                  Email уже установлен и не может быть изменен
                </p>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="border-t border-white/10 pt-4 mb-6">
              <TermsAndConditions
                terms={terms}
                onTermsChange={handleTermsChange}
                disabled={paying}
              />
            </div>

            {paymentError && (
              <p className="mb-4 text-sm text-red-300">{paymentError}</p>
            )}
            <p className="mb-4 text-sm text-amber-200/90">
              Оплата временно недоступна: сервис прекратил приём платежей.
            </p>

            {/* Payment Button */}
            <button
              onClick={handlePay}
              disabled
              className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white/10 text-white/50 cursor-not-allowed"
            >
              <span>Оплата недоступна</span>
            </button>
          </div>

          {/* Security Info Panel */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 text-white text-base leading-relaxed">
                Оплата производится через защищенное соединение. Ваши данные защищены и не передаются третьим лицам.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
